import React, { useEffect, useMemo, useState } from 'react'
import { Alert, DatePicker, Form, Select, Table, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  AnnualSummaryMonth,
  ReportSourceKey,
  SourceAmounts,
  useAnnualSummaryQuery,
} from '../../api/reconciliation-v2-reports-api-client'
import { sourceLabel } from '../../components/TransactionDetails/helpers'
import {
  ANNUAL_SOURCE_COLUMNS,
  CORE_SOURCE_COLUMNS,
  currentMonthLA,
  formatReportMoney,
  reportSourceLabel,
} from './helpers'
import styles from './ReconciliationV2Reports.module.scss'

const { Text } = Typography

type SectionTone = 'premiums' | 'paid' | 'unpaid'

/** Columns shown for a table: the core sources plus any other with money. */
const visibleColumns = (rows: SourceAmounts[]): ReportSourceKey[] =>
  ANNUAL_SOURCE_COLUMNS.filter(
    (key) =>
      CORE_SOURCE_COLUMNS.has(key) ||
      rows.some((row) => row.bySource[key] !== 0)
  )

/**
 * From the month's own first-of-month date — never `dayjs().month(n)`, which
 * keeps today's day-of-month and overflows on the 29th–31st (Feb → March).
 */
export const monthName = ({ periodStartDate }: AnnualSummaryMonth): string =>
  dayjs(periodStartDate).format('MMMM')

interface SourceTableProps {
  title: string
  tone: SectionTone
  months: AnnualSummaryMonth[]
  pick: (month: AnnualSummaryMonth) => SourceAmounts
  yearTotal: SourceAmounts
  countTitle?: string
  loading: boolean
}

/**
 * One spreadsheet block: a row per month, a column per source, a TOTAL and an
 * optional count, with a year-total footer. Months after the current one stay
 * blank — nothing is due yet.
 */
const SourceTable = ({
  title,
  tone,
  months,
  pick,
  yearTotal,
  countTitle,
  loading,
}: SourceTableProps) => {
  const sourceKeys = visibleColumns(months.map(pick))
  const columns: TableColumnsType<AnnualSummaryMonth> = [
    {
      title: 'Month',
      key: 'month',
      render: (_, month) => monthName(month),
    },
    ...sourceKeys.map((key) => ({
      title: reportSourceLabel(key),
      key,
      align: 'right' as const,
      render: (_: unknown, month: AnnualSummaryMonth) =>
        month.isFuture ? '' : formatReportMoney(pick(month).bySource[key]),
    })),
    {
      title: 'TOTAL',
      key: 'total',
      align: 'right',
      render: (_, month) => (
        <Text strong>
          {month.isFuture ? '' : formatReportMoney(pick(month).total)}
        </Text>
      ),
    },
    ...(countTitle
      ? [
          {
            title: countTitle,
            key: 'count',
            align: 'right' as const,
            render: (_: unknown, month: AnnualSummaryMonth) =>
              month.isFuture ? '' : pick(month).count,
          },
        ]
      : []),
  ]

  return (
    <section className={styles.section} aria-label={title}>
      <div className={`${styles.sectionHeader} ${styles[tone]}`}>{title}</div>
      <Table<AnnualSummaryMonth>
        rowKey="month"
        size="small"
        columns={columns}
        dataSource={months}
        loading={loading}
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Text strong>Year Total</Text>
            </Table.Summary.Cell>
            {sourceKeys.map((key, index) => (
              <Table.Summary.Cell key={key} index={index + 1} align="right">
                <Text strong>{formatReportMoney(yearTotal.bySource[key])}</Text>
              </Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={sourceKeys.length + 1} align="right">
              <Text strong>{formatReportMoney(yearTotal.total)}</Text>
            </Table.Summary.Cell>
            {countTitle && (
              <Table.Summary.Cell index={sourceKeys.length + 2} align="right">
                <Text strong>{yearTotal.count}</Text>
              </Table.Summary.Cell>
            )}
          </Table.Summary.Row>
        )}
      />
    </section>
  )
}

/**
 * Reconciliation annual summary — Finance's year spreadsheet: Total Premiums
 * by premium source (the paying source when paid, else the benefit's current
 * assigned method), Unpaid by assigned method, Paid by the paying source, YTD
 * unreconciled transactions, and Unpaid New Enrollments for a chosen month.
 */
export default function AnnualSummaryTab() {
  const [year, setYear] = useState<number>(() => currentMonthLA().year())
  const { data, isLoading, isFetching, error } = useAnnualSummaryQuery(year)
  const defaultNewEnrollmentMonth =
    year === currentMonthLA().year() ? currentMonthLA().month() + 1 : 12
  const [newEnrollmentMonth, setNewEnrollmentMonth] = useState(
    defaultNewEnrollmentMonth
  )
  useEffect(() => {
    setNewEnrollmentMonth(defaultNewEnrollmentMonth)
  }, [defaultNewEnrollmentMonth])

  const months = data?.months ?? []
  const loading = isLoading || isFetching
  const newEnrollments = months.find(
    (month) => month.month === newEnrollmentMonth
  )?.newEnrollmentsUnpaid
  const newEnrollmentKeys = useMemo(
    () => visibleColumns(newEnrollments ? [newEnrollments] : []),
    [newEnrollments]
  )

  return (
    <>
      <Form.Item label="Year" layout="vertical">
        <DatePicker
          picker="year"
          allowClear={false}
          value={dayjs(`${year}-01-01`)}
          onChange={(value: Dayjs | null) => value && setYear(value.year())}
          aria-label="Year"
        />
      </Form.Item>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Could not load the annual summary."
          style={{ marginBottom: 16 }}
        />
      )}

      {data && (
        <>
          <SourceTable
            title="Total Premiums"
            tone="premiums"
            months={months}
            pick={(month) => month.premiums}
            yearTotal={data.yearTotals.premiums}
            loading={loading}
          />
          <SourceTable
            title="PAID"
            tone="paid"
            months={months}
            pick={(month) => month.paid}
            yearTotal={data.yearTotals.paid}
            loading={loading}
          />
          <SourceTable
            title="UNPAID"
            tone="unpaid"
            months={months}
            pick={(month) => month.unpaid}
            yearTotal={data.yearTotals.unpaid}
            countTitle="Count Unpaid"
            loading={loading}
          />

          <div className={styles.bottomRow}>
            <section
              className={styles.section}
              aria-label="YTD Unreconciled payments"
            >
              <div className={`${styles.sectionHeader} ${styles.neutral}`}>
                YTD Unreconciled payments
              </div>
              <Table
                rowKey="source"
                size="small"
                pagination={false}
                bordered
                loading={loading}
                dataSource={data.unreconciled.rows}
                columns={[
                  {
                    title: 'Vendor',
                    dataIndex: 'source',
                    render: (source: string) => sourceLabel(source),
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    align: 'right',
                    render: (amount: number) => formatReportMoney(amount),
                  },
                  { title: 'Count', dataIndex: 'count', align: 'right' },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>
                        {formatReportMoney(data.unreconciled.totalAmount)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{data.unreconciled.totalCount}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
              <div className={styles.absorbed}>
                <Text type="secondary">Confirmed absorbing</Text>
                <Text strong>
                  {formatReportMoney(data.unreconciled.absorbed)}
                </Text>
              </div>
            </section>

            <section
              className={styles.section}
              aria-label="Unpaid New Enrollments"
            >
              <div className={`${styles.sectionHeader} ${styles.unpaid}`}>
                <span>Unpaid New Enrollments</span>
                <Select
                  size="small"
                  value={newEnrollmentMonth}
                  onChange={setNewEnrollmentMonth}
                  aria-label="New enrollment month"
                  style={{ width: 130 }}
                  options={months.map((month) => ({
                    value: month.month,
                    label: monthName(month),
                  }))}
                />
              </div>
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                bordered
                loading={loading}
                scroll={{ x: 'max-content' }}
                dataSource={
                  newEnrollments ? [{ key: 'row', ...newEnrollments }] : []
                }
                columns={[
                  ...newEnrollmentKeys.map((key) => ({
                    title: reportSourceLabel(key),
                    key,
                    align: 'right' as const,
                    render: (_: unknown, row: SourceAmounts) =>
                      formatReportMoney(row.bySource[key]),
                  })),
                  {
                    title: 'TOTAL',
                    key: 'total',
                    align: 'right' as const,
                    render: (_: unknown, row: SourceAmounts) => (
                      <Text strong>{formatReportMoney(row.total)}</Text>
                    ),
                  },
                  {
                    title: 'Count',
                    key: 'count',
                    align: 'right' as const,
                    render: (_: unknown, row: SourceAmounts) => row.count,
                  },
                ]}
              />
              <Text type="secondary" className={styles.note}>
                Benefits whose coverage starts in this month with no payment
                allocated yet.
              </Text>
            </section>
          </div>
        </>
      )}
    </>
  )
}
